import {
  findSticker,
  findStickerBySlug,
  createUserInvite,
  findPublishedStickers,
  findPublishedStickersWithFiltering,
  findPurchasedStickers,
  checkUserExists,
  benevityCauses,
  getHomeScreenAppData,
  findPublicStaticPage,
  findOrganizationsByIds,
  findStickersByIds,
  mutateFeaturedQuestionAnswer,
  organization,
  findOrganizationsV2,
  findPublishedStickersShortDetails,
  findPublishedStickersShortDetailsWithSocialShop,
  findOrgStickers,
  findApprovedOrgStickers,
  findApprovedOrgStickersBySlug,
} from '@/graphs/gql';
import { signUp } from '@/graphs/invite';
import { createStickerWebIntent, curatedBenevityCauses, createStickerWebIntentPublic } from '@/graphs/payment';
import { isStickerPurchased, findPurchasedStickersHistory, toggleStickerFavorite, findMyFavorites } from '@/graphs/stickers';
import globalShared from '@/shared';
import { getProfile, getUserOrganizations, userPublicProfile, userPublicProfileBySlug } from '@/graphs/profile';
import { organizationPublicProfile, organizationPublicProfileBySlug } from '@/graphs/organization';

export interface FetchUserOrganizationsResult {
  profile?: any;
  organizations: Array<{ id?: unknown; orgId?: unknown }>;
  source: string;
  error?: string;
  graphqlErrors?: string[];
  organizationCount: number;
}

function collectGraphqlErrors(response: any): string[] {
  return (response?.errors ?? []).map((entry: { message?: string }) => entry.message || 'Unknown error');
}

function extractUserManagedOrganizations(
  profile: any,
): Array<{ id?: unknown; orgId?: unknown }> {
  const userOrganizations = profile?.user?.organizations;
  if (Array.isArray(userOrganizations) && userOrganizations.length) {
    return userOrganizations;
  }

  const profileOrganizations = profile?.organizations;
  return Array.isArray(profileOrganizations) ? profileOrganizations : [];
}

function persistUserManagedOrganizations(
  store: { commit: (type: string, payload?: unknown) => void },
  organizations: Array<{ id?: unknown; orgId?: unknown }>,
): void {
  store.commit('organizations/MUTATE_USER_MANAGED_ORGANIZATIONS', organizations);
}

export default function commonShared(): { [key: string]: any } {
  const {
    ref,
    route,
    router,
    store,
    removeItemGetConfirm,
    makeGraphRequest,
    getAuthToken,
    preSignedUrl,
  } = globalShared();

  async function getPublicSticker(id: number): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findSticker,
      { id },
      null,
    );
    return response.data.findSticker;
  }

  async function getPublicStickerBySlug(slug: string): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findStickerBySlug,
      { slug },
      null,
    );
    return response.data.findStickerBySlug;
  }

  async function getSticker(id: number): Promise<any> {
    const response = await makeGraphRequest('query', findSticker, { id });
    return response.data.findSticker;
  }

  async function getStickerBySlug(slug: string): Promise<any> {
    const response = await makeGraphRequest('query', findStickerBySlug, { slug });
    return response.data.findStickerBySlug;
  }

  async function getPublishedStickers(take = 10, skip = 0, filter: string[] = [], searchString = ''): Promise<any> {
    const response = await makeGraphRequest('query', findPublishedStickers, {
      filter, take, skip, searchString,
    }, getAuthToken());
    return response.data.findPublishedStickers;
  }

  async function getPublishedPublicStickers(take = 10, skip = 0, filter: string[] = [], searchString = '', socialShopUserId?: string): Promise<any> {
    const variables: any = { filter, take, skip, searchString };
    if (socialShopUserId) {
      variables.socialShopUserId = socialShopUserId;
    }
    const response = await makeGraphRequest('query', findPublishedStickersShortDetails, variables, null);
    return response.data.findPublishedStickersShortDetails;
  }

  async function getSocialShopStickers(options: {
    socialShopOrgId?: string;
    socialShopUserId?: string;
    socialShopGroupId?: string;
    take?: number;
    skip?: number;
  } = {}): Promise<any[]> {
    const {
      socialShopOrgId,
      socialShopUserId,
      socialShopGroupId,
      take = 1000,
      skip = 0,
    } = options;

    const variables: any = {
      filter: ['Active'],
      take,
      skip,
    };

    if (socialShopOrgId) {
      variables.socialShopOrgId = socialShopOrgId;
    }
    if (socialShopUserId) {
      variables.socialShopUserId = socialShopUserId;
    }
    if (socialShopGroupId) {
      variables.socialShopGroupId = socialShopGroupId;
    }

    const authToken = store.getters['users/me']?.token || null;
    const response = await makeGraphRequest(
      'query',
      findPublishedStickersShortDetailsWithSocialShop,
      variables,
      authToken,
    );

    if (response?.errors?.length) {
      console.error('Social shop stickers query errors:', response.errors);
    }

    const stickers = response?.data?.findPublishedStickersShortDetails || [];

    return [...stickers].sort((a, b) => {
      const orderA = a?.socialShop?.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b?.socialShop?.sortOrder ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  async function getPublishedStickersWithFiltering(filter: any, take = 10, skip = 0, searchString = ''): Promise<any> {
    const response = await makeGraphRequest('query', findPublishedStickersWithFiltering, {
      filter, take, skip, searchString,
    }, getAuthToken());
    return response.data.findPublishedStickers;
  }

  async function getPurchasedStickers(entity: string, entityId: string): Promise<any> {
    const response = await makeGraphRequest('query', findPurchasedStickers, { entity, entityId }, getAuthToken());
    return response.data.findPurchasedStickers;
  }

  async function getPurchasedStickersHistory(
    entity: string,
    entityId: string,
    skip = 0,
    take = 10,
    searchString = '',
    stickerType?: string,
    timeFilter?: string,
  ): Promise<any> {
    const variables: any = {
      entity, entityId, skip, take, searchString,
    };
    
    if (stickerType) {
      variables.stickerType = stickerType;
    }
    
    if (timeFilter) {
      variables.timeFilter = timeFilter;
    }
    
    const response = await makeGraphRequest(
      'query',
      findPurchasedStickersHistory,
      variables,
      getAuthToken(),
    );
    return response.data.findPurchasedStickersHistory;
  }

  async function doCreateUserInvite(input: any): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      createUserInvite,
      { input },
      null,
    );
    return response.data.createUserInvite;
  }

  async function doSignUp(input: any): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      signUp,
      { input },
      null,
    );
    return response.data.signUp;
  }

  type CreateStickerWebIntentVariables = {
    stickerId: string,
    causeId: string,
    cost?: string,
    socialShopId?: string | null,
    glowId?: string | null,
  }

  async function doCreateStickerWebIntent(
    stickerId: string,
    causeId: string,
    customAmount?: number | string,
    socialShopId?: string,
    glowId?: string,
  ): Promise<any> {
    const variables: CreateStickerWebIntentVariables = { 
      stickerId, 
      causeId,
      socialShopId: socialShopId || null,
      glowId: glowId || null,
    };

    if (customAmount !== undefined) {
      variables.cost = customAmount.toString();
    }

    const response = await makeGraphRequest(
      'mutation',
      createStickerWebIntent,
      variables,
      getAuthToken(),
    );
    return response.data.createStickerWebIntent;
  }
  async function doCreateStickerWebIntentPublic(
    stickerId: string,
    causeId: string,
    customAmount?: number | string,
    socialShopId?: string,
    glowId?: string,
  ): Promise<any> {
    const variables: CreateStickerWebIntentVariables = { 
      stickerId, 
      causeId,
      socialShopId: socialShopId || null,
      glowId: glowId || null,
    };

    if (customAmount !== undefined) {
      variables.cost = customAmount.toString();
    }

    const response = await makeGraphRequest(
      'mutation',
      createStickerWebIntentPublic,
      variables,
    );
    return response.data.createStickerWebIntentPublic;
  }

  // eslint-disable-next-line max-len
  async function checkIsStickerPurchased(
    entity: string,
    entityId: string,
    stickerId: string,
  ): Promise<any> {
    const response = await makeGraphRequest('query', isStickerPurchased, { entity, entityId, stickerId }, getAuthToken());
    return response.data.isStickerPurchased;
  }

  async function doCheckUserExists(phone?: string, email?: string, userId?: string): Promise<any> {
    const variables: { phone?: string; email?: string; userId?: string } = {};
    if (phone) variables.phone = phone;
    if (email) variables.email = email;
    if (userId) variables.userId = userId;
    const response = await makeGraphRequest('query', checkUserExists, variables, null);
    return response;
  }

  async function getCuratedBenevityCauses(): Promise<any> {
    const response = await makeGraphRequest('query', curatedBenevityCauses, {}, null);
    return response.data.curatedBenevityCauses;
  }

  async function getcausesAsDropdown(query: string): Promise<Array<Map<string, any>>> {
    const response = await makeGraphRequest('query', benevityCauses, { q: query, page: '1' }, getAuthToken());
    return response.data.benevityCauses.causes;
  }

  async function fetchHomeScreenAppData(): Promise<any> {
    const response = await makeGraphRequest('query', getHomeScreenAppData, {}, null);
    return response.data.getHomeScreenAppData;
  }

  async function fetchPublicStaticPage(slug: string): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findPublicStaticPage,
      { isId: false, idOrSlug: slug },
      null,
    );

    if (response?.errors?.length) {
      throw new Error(response.errors[0]?.message || 'Failed to load page');
    }

    return response?.data?.findPublicStaticPage ?? null;
  }

  async function fetchOrganizationsByIds(orgIds: string[]): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findOrganizationsByIds,
      { orgIds },
      null,
    );
    return response.data.findOrganizationsByIds;
  }

  async function fetchStickersByIds(stickerIds: string[]): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findStickersByIds,
      { stickerIds },
      null,
    );
    return response.data.findStickersByIds;
  }

  async function submitFeaturedQuestionAnswer(
    questionId: string,
    content: string,
  ): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      mutateFeaturedQuestionAnswer,
      { questionId, input: { content } },
      null,
    );
    return response.data.mutateFeaturedQuestionAnswer;
  }

  async function fetchProfile(): Promise<any> {
    const token = getAuthToken();
    if (!token) {
      router.push('/auth/signin');
      return null;
    }

    const response = await makeGraphRequest('query', getProfile, {}, token);
    return response.data.profile;
  }

  async function fetchUserOrganizations(): Promise<FetchUserOrganizationsResult> {
    const token = getAuthToken();
    if (!token) {
      return {
        organizations: [],
        source: 'no-token',
        error: 'No auth token available',
        organizationCount: 0,
      };
    }

    const attempts: Array<{
      source: string;
      run: () => Promise<any>;
      extract: (response: any) => {
        profile: any;
        organizations: Array<{ id?: unknown; orgId?: unknown }>;
      };
    }> = [
      {
        source: 'getUserOrganizations',
        run: () => makeGraphRequest('query', getUserOrganizations, {}, token),
        extract: (response) => {
          const profile = response?.data?.profile ?? null;
          return {
            profile,
            organizations: extractUserManagedOrganizations(profile),
          };
        },
      },
      {
        source: 'getProfile.user.organizations',
        run: () => makeGraphRequest('query', getProfile, {}, token),
        extract: (response) => {
          const profile = response?.data?.profile ?? null;
          return {
            profile,
            organizations: extractUserManagedOrganizations(profile),
          };
        },
      },
    ];

    const graphqlErrors: string[] = [];

    for (const attempt of attempts) {
      try {
        const response = await attempt.run();
        graphqlErrors.push(...collectGraphqlErrors(response).map(
          (message) => `${attempt.source}: ${message}`,
        ));

        const { profile, organizations } = attempt.extract(response);
        if (organizations.length) {
          persistUserManagedOrganizations(store, organizations);
          return {
            profile,
            organizations,
            source: attempt.source,
            organizationCount: organizations.length,
            graphqlErrors: graphqlErrors.length ? graphqlErrors : undefined,
          };
        }
      } catch (err) {
        graphqlErrors.push(`${attempt.source}: ${(err as Error)?.message || String(err)}`);
      }
    }

    persistUserManagedOrganizations(store, []);

    return {
      organizations: [],
      source: 'none',
      error: 'All organization fetch attempts returned empty',
      organizationCount: 0,
      graphqlErrors: graphqlErrors.length ? graphqlErrors : undefined,
    };
  }

  async function fetchPublicProfile(userId: string): Promise<any> {
    const response = await makeGraphRequest('query', userPublicProfile, { id: userId });
    return response.data.userPublicProfile;
  }

  async function fetchPublicProfileBySlug(slug: string): Promise<any> {
    const response = await makeGraphRequest('query', userPublicProfileBySlug, { slug });
    return response.data.userPublicProfileBySlug;
  }

  async function fetchOrganization(orgId: string): Promise<any> {
    const token = getAuthToken();
    if (!token) {
      router.push('/auth/signin');
      return null;
    }

    const response = await makeGraphRequest('query', organization, { id: orgId }, token);
    return response.data.getOrganization;
  }

  async function fetchPublicOrganization(orgId: string): Promise<any> {
    const response = await makeGraphRequest('query', organizationPublicProfile, { id: orgId }, null);
    return response.data.organizationPublicProfile;
  }

  async function fetchPublicOrganizationBySlug(slug: string): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      organizationPublicProfileBySlug,
      { slug },
      null,
    );
    return response.data.organizationPublicProfileBySlug;
  }

  async function fetchAllOrganizations(take = 50, skip = 0, searchString = ''): Promise<any> {
    const token = getAuthToken();
    if (!token) {
      router.push('/auth/signin');
      return null;
    }

    const baseInput = {
      ids: null,
      entity: null,
      entityIds: null,
      isActive: null,
      statuses: 'Approved',
      excludeUnclaimed: true,
      query: searchString || null,
      page: Math.floor(skip / take) + 1,
      pageSize: take,
    };

    const response = await makeGraphRequest('query', findOrganizationsV2, { baseInput }, token);
    return response.data.findOrganizationsV2;
  }

  async function loadMyOrganizations(): Promise<any[]> {
    const token = getAuthToken();
    if (!token) {
      return [];
    }

    const organizations = await fetchAllOrganizations(50, 0, '');
    const list = Array.isArray(organizations) ? organizations : [];
    persistUserManagedOrganizations(store, list);
    return list;
  }

  async function getOrgStickers(orgId: string): Promise<any> {
    const response = await makeGraphRequest('query', findOrgStickers, { orgId }, getAuthToken());
    return response.data.findOrgStickers;
  }

  async function fetchApprovedOrgStickers(orgId: string, searchString?: string): Promise<any> {
    const variables = { orgId, ...(searchString && { searchString }) };
    const response = await makeGraphRequest('query', findApprovedOrgStickers, variables, null);
    return response.data.findApprovedOrgStickers;
  }

  async function fetchApprovedOrgStickersBySlug(slug: string, searchString?: string): Promise<any> {
    const variables = { slug, ...(searchString && { searchString }) };
    const response = await makeGraphRequest('query', findApprovedOrgStickersBySlug, variables, null);
    return response.data.findApprovedOrgStickersBySlug;
  }

  async function toggleFavorite(stickerId: string, orgId?: string): Promise<boolean> {
    const response = await makeGraphRequest(
      'mutation',
      toggleStickerFavorite,
      { stickerId, orgId },
      getAuthToken(),
    );
    return response.data.toggleStickerFavorite;
  }

  async function getMyFavorites(orgId?: string): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findMyFavorites,
      { orgId },
      getAuthToken(),
    );
    return response.data.findMyFavorites;
  }

  return {
    ref,
    route,
    router,
    loading: ref(false),
    dialog: ref(false),
    removeItemGetConfirm,
    makeGraphRequest,
    preSignedUrl,

    getSticker,
    getStickerBySlug,
    doCreateUserInvite,
    getPublishedStickers,
    getPublishedStickersWithFiltering,
    getPurchasedStickers,
    getPurchasedStickersHistory,
    doCreateStickerWebIntent,
    doCreateStickerWebIntentPublic,
    checkIsStickerPurchased,
    getPublicSticker,
    getPublicStickerBySlug,
    doSignUp,
    doCheckUserExists,
    getCuratedBenevityCauses,
    getcausesAsDropdown,
    fetchHomeScreenAppData,
    fetchPublicStaticPage,
    fetchOrganizationsByIds,
    fetchStickersByIds,
    submitFeaturedQuestionAnswer,
    fetchProfile,
    fetchUserOrganizations,
    fetchPublicProfile,
    fetchPublicProfileBySlug,
    fetchOrganization,
    fetchPublicOrganization,
    fetchPublicOrganizationBySlug,
    fetchAllOrganizations,
    loadMyOrganizations,
    getPublishedPublicStickers,
    getSocialShopStickers,
    getOrgStickers,
    fetchApprovedOrgStickers,
    fetchApprovedOrgStickersBySlug,
    toggleFavorite,
    getMyFavorites,
  };
}
